import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Crown,
  Loader2,
  Lock,
  RefreshCw,
  Settings2,
  Sparkles,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { ProgressStepper } from "@/components/progress-stepper";
import { TranscriptFallback } from "@/components/transcript-fallback";
import { ApiError, getConfig, getQuiz, submitAttempt, updateQuizOptions } from "@/lib/api";
import { LEVEL_NAMES } from "@/lib/levels";
import { defaultsForCounts } from "@/lib/url";
import { useVideoJob } from "@/hooks/useVideoJob";
import type { PublicQuiz, QuizAttemptResult, QuizLevel, VideoLevelStatus, VideoMode } from "@lib/domain";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const OPTION_PREFIXES = ["A", "B", "C", "D", "E", "F"] as const;

function LevelSelector({
  levels,
  activeLevel,
  onSelect,
}: {
  levels: VideoLevelStatus[];
  activeLevel: QuizLevel;
  onSelect: (level: QuizLevel) => void;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {levels.map((status) => {
        const active = status.level === activeLevel;
        const locked = !status.unlocked;
        return (
          <button
            key={status.level}
            type="button"
            disabled={locked}
            onClick={() => onSelect(status.level)}
            className={`rounded-lg border p-4 text-left transition-colors ${
              active
                ? "border-primary bg-primary/10"
                : locked
                  ? "cursor-not-allowed border-muted bg-muted/30 opacity-70"
                  : "bg-transparent hover:border-primary/60 hover:bg-accent"
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold">
                Level {status.level} · {status.name}
              </span>
              {!status.available ? (
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              ) : locked ? (
                <Lock className="size-4 text-muted-foreground" />
              ) : status.passed ? (
                <CheckCircle2 className="size-4 text-emerald-600" />
              ) : (
                <Sparkles className="size-4 text-primary/70" />
              )}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{status.tagline}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {!status.available
                ? "Generating…"
                : locked
                  ? `Locked — pass Level ${status.level - 1}`
                  : status.questionCount !== null
                    ? `${status.questionCount} questions`
                    : "—"}
            </p>
          </button>
        );
      })}
    </div>
  );
}

function EditCounts({
  current,
  min,
  max,
  editRequiresPayment,
  onApply,
}: {
  current: Record<1 | 2 | 3, number>;
  min: number;
  max: number;
  editRequiresPayment: boolean;
  onApply: (counts: Record<1 | 2 | 3, number>, paid: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState(current);
  const [paid, setPaid] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!open) {
    return (
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
          <Settings2 />
          Change question count
        </Button>
      </div>
    );
  }

  const setCount = (level: 1 | 2 | 3, value: string) => {
    const n = Number.parseInt(value, 10);
    setCounts((prev) => ({ ...prev, [level]: Number.isFinite(n) ? n : prev[level] }));
  };

  const save = async (isPaid: boolean) => {
    setSaving(true);
    try {
      onApply(
        {
          1: Math.min(max, Math.max(min, counts[1])),
          2: Math.min(max, Math.max(min, counts[2])),
          3: Math.min(max, Math.max(min, counts[3])),
        },
        isPaid,
      );
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border p-3">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-medium">Questions per level</p>
        <Badge variant="outline" className="text-xs">
          {min}–{max}
        </Badge>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {([1, 2, 3] as const).map((level) => (
          <div key={level} className="space-y-1">
            <Label htmlFor={`edit-count-${level}`} className="text-xs">
              Level {level} · {LEVEL_NAMES[level]}
            </Label>
            <Input
              id={`edit-count-${level}`}
              type="number"
              min={min}
              max={max}
              value={counts[level]}
              onChange={(e) => setCount(level, e.target.value)}
              className="w-20"
            />
          </div>
        ))}
      </div>
      {editRequiresPayment && !paid && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Crown className="size-3.5 text-amber-500" />
          Changing the count on an existing quiz requires a paid plan.
        </p>
      )}
      <div className="mt-3 flex items-center gap-2">
        <Button size="sm" disabled={saving} onClick={() => void save(paid)}>
          Apply
        </Button>
        {editRequiresPayment && !paid && (
          <Button
            size="sm"
            variant="secondary"
            disabled={saving}
            onClick={() => {
              setPaid(true);
              void save(true);
            }}
          >
            <Crown />
            Pay &amp; apply
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function QuizCard({
  quiz,
  onSubmitted,
}: {
  quiz: PublicQuiz;
  onSubmitted: (result: QuizAttemptResult) => void;
}) {
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>(new Array(quiz.questions.length).fill(null));
  const [submitting, setSubmitting] = useState(false);

  const question = quiz.questions[index];
  if (!question) return null;

  const selected = answers[index] ?? null;
  const answeredCount = answers.filter((a) => a !== null).length;
  const isLast = index === quiz.questions.length - 1;

  const choose = (optionIndex: number) => {
    setAnswers((prev) => {
      const next = prev.slice();
      next[index] = optionIndex;
      return next;
    });
  };

  const submit = async () => {
    const finalAnswers = answers.map((a, i) => a ?? i);
    setSubmitting(true);
    try {
      const result = await submitAttempt(quiz.videoId, finalAnswers, quiz.level);
      onSubmitted(result);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Could not submit your answers.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-lg">Question {index + 1} of {quiz.questions.length}</CardTitle>
          {question.scope === "beyond" && (
            <Badge variant="secondary">Beyond the video</Badge>
          )}
        </div>
        <CardDescription>
          Level {quiz.level} · {LEVEL_NAMES[quiz.level]} — select the best answer, then continue.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Progress value={((index + 1) / quiz.questions.length) * 100} />

        <p className="text-base font-medium leading-relaxed">{question.question}</p>

        <div className="grid gap-2">
          {question.options.map((option, optionIndex) => {
            const prefix = OPTION_PREFIXES[optionIndex] ?? String(optionIndex + 1);
            const isSelected = selected === optionIndex;
            return (
              <button
                key={optionIndex}
                type="button"
                onClick={() => choose(optionIndex)}
                className={`flex items-center gap-3 rounded-md border px-4 py-3 text-left text-sm transition-colors ${
                  isSelected
                    ? "border-primary bg-primary/10 text-foreground"
                    : "bg-transparent hover:border-primary/60 hover:bg-accent"
                }`}
              >
                <span
                  className={`inline-flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold ${
                    isSelected ? "border-primary bg-primary text-primary-foreground" : "text-muted-foreground"
                  }`}
                >
                  {prefix}
                </span>
                <span className="flex-1">{option}</span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" disabled={index === 0} onClick={() => setIndex((i) => i - 1)}>
            <ArrowLeft />
            Back
          </Button>
          <span className="text-xs text-muted-foreground">
            {answeredCount} of {quiz.questions.length} answered
          </span>
          {isLast ? (
            <Button
              size="sm"
              disabled={submitting || answeredCount < quiz.questions.length}
              onClick={() => void submit()}
            >
              <Check />
              Submit answers
            </Button>
          ) : (
            <Button size="sm" disabled={selected === null} onClick={() => setIndex((i) => i + 1)}>
              Next
              <ArrowRight />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ResultsView({
  result,
  nextLevel,
  onNext,
}: {
  result: QuizAttemptResult;
  nextLevel: VideoLevelStatus | null;
  onNext: () => void;
}) {
  return (
    <div className="space-y-6">
      <Card className="text-center">
        <CardHeader>
          <Badge
            variant={result.passed ? "success" : "destructive"}
            className="mx-auto w-fit text-sm"
          >
            {result.passed ? "Passed" : "Not passed"}
          </Badge>
          <CardTitle className="mt-2 text-3xl font-bold">
            {result.percent}%
          </CardTitle>
          <CardDescription>
            You got {result.score} of {result.total} questions correct on Level {result.level}{" "}
            ({LEVEL_NAMES[result.level]}).
          </CardDescription>
        </CardHeader>
      </Card>

      {result.passed && nextLevel?.available && (
        <Button size="lg" className="w-full gap-2" onClick={onNext}>
          Continue to Level {nextLevel.level} · {nextLevel.name}
          <ArrowRight />
        </Button>
      )}

      <div className="space-y-3">
        {result.results.map((r, i) => (
          <Card key={r.questionId}>
            <CardContent className="flex items-start gap-3 p-4">
              {r.isCorrect ? (
                <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
                  <Check className="size-3" />
                </span>
              ) : (
                <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-destructive text-white">
                  <X className="size-3" />
                </span>
              )}
              <div className="flex-1 space-y-1">
                <p className="text-sm font-medium">Question {i + 1}</p>
                <p className="text-sm text-muted-foreground">
                  {r.isCorrect ? (
                    <>Correct — you picked option {r.correctIndex + 1}.</>
                  ) : (
                    <>
                      You picked option {r.selected + 1}; the correct answer was option{" "}
                      {r.correctIndex + 1}.
                    </>
                  )}
                </p>
                {r.explanation && <p className="text-sm text-foreground">{r.explanation}</p>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export function QuizPage() {
  const { videoId = "" } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCounts = useMemo(
    () => defaultsForCounts(searchParams.get("counts")),
    [searchParams],
  );
  const mode: VideoMode = searchParams.get("mode") === "text" ? "text" : "quiz-only";
  const { summary, error, loading, refresh, retry } = useVideoJob(videoId, mode, initialCounts);
  const [levels, setLevels] = useState<VideoLevelStatus[]>([]);
  const [activeLevel, setActiveLevel] = useState<QuizLevel>(1);
  const [quiz, setQuiz] = useState<PublicQuiz | null>(null);
  const [result, setResult] = useState<QuizAttemptResult | null>(null);
  const [levelError, setLevelError] = useState<string | null>(null);
  const [loadingQuiz, setLoadingQuiz] = useState(false);
  const [countBounds, setCountBounds] = useState({ min: 5, max: 20, editRequiresPayment: false });

  const ready = summary?.status === "ready";
  const anyLevelAvailable = levels.some((l) => l.available);
  const activeLevelStatus = useMemo(
    () => levels.find((l) => l.level === activeLevel) ?? null,
    [levels, activeLevel],
  );
  const activeAvailable = activeLevelStatus?.available ?? false;
  const activeUnlocked = activeLevelStatus?.unlocked ?? false;
  const activeQuestionCount = activeLevelStatus?.questionCount ?? null;

  useEffect(() => {
    let cancelled = false;
    getConfig()
      .then((config) => {
        if (cancelled) return;
        setCountBounds({
          min: config.minQuestionsPerLevel,
          max: config.maxQuestionsPerLevel,
          editRequiresPayment: config.editRequiresPayment,
        });
      })
      .catch(() => {
        /* defaults are fine */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const currentCounts = useMemo<Record<1 | 2 | 3, number>>(() => {
    if (!ready || levels.length === 0) return initialCounts;
    return {
      1: levels.find((l) => l.level === 1)?.questionCount ?? initialCounts[1],
      2: levels.find((l) => l.level === 2)?.questionCount ?? initialCounts[2],
      3: levels.find((l) => l.level === 3)?.questionCount ?? initialCounts[3],
    };
  }, [ready, levels, initialCounts]);

  const applyCounts = (next: Record<1 | 2 | 3, number>, paid: boolean) => {
    void (async () => {
      try {
        await updateQuizOptions(videoId, next, paid);
        toast.success("Question counts updated.");
        refresh();
      } catch (e) {
        toast.error(e instanceof ApiError ? e.message : "Could not update question counts.");
      }
    })();
  };

  useEffect(() => {
    if (summary?.levels) setLevels(summary.levels);
  }, [summary]);

  useEffect(() => {
    // Serve a level as soon as it exists rather than waiting for the whole
    // ladder: Level 1 is generated far sooner than Understand + Beyond.
    if (!activeAvailable || !activeUnlocked) return;
    let cancelled = false;
    setResult(null);
    setQuiz(null);
    setLevelError(null);

    setLoadingQuiz(true);
    getQuiz(videoId, activeLevel)
      .then((q) => {
        if (!cancelled) setQuiz(q);
      })
      .catch((e: unknown) => {
        if (!cancelled) setLevelError(e instanceof ApiError ? e.message : "Could not load the quiz.");
      })
      .finally(() => {
        if (!cancelled) setLoadingQuiz(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeAvailable, activeUnlocked, activeLevel, activeQuestionCount, videoId]);

  const nextLevel = useMemo(
    () => levels.find((l) => l.level === (activeLevel + 1) as QuizLevel) ?? null,
    [levels, activeLevel],
  );

  const onSubmitted = (r: QuizAttemptResult) => {
    setResult(r);
    if (r.passed) {
      setLevels((prev) =>
        prev.map((l) => {
          if (l.level === r.level) {
            return { ...l, passed: true, bestPercent: Math.max(l.bestPercent ?? 0, r.percent) };
          }
          if (l.level === ((r.level + 1) as QuizLevel) && l.available) {
            return { ...l, unlocked: true };
          }
          return l;
        }),
      );
    }
  };

  const goToNextLevel = () => {
    if (nextLevel) setActiveLevel(nextLevel.level);
  };

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">
            {result ? "Your results" : quiz ? `Level ${activeLevel} · ${LEVEL_NAMES[activeLevel]}` : "Preparing your quiz"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {quiz
              ? `${quiz.questions.length} questions · built with ${quiz.model}`
              : "Three levels, each harder and broader than the last."}
          </p>
        </div>
        <Badge variant="outline" className="font-mono text-xs">
          {videoId}
        </Badge>
      </div>

      {anyLevelAvailable && (
        <LevelSelector levels={levels} activeLevel={activeLevel} onSelect={setActiveLevel} />
      )}

      {ready && !result && (
        <EditCounts
          current={currentCounts}
          min={countBounds.min}
          max={countBounds.max}
          editRequiresPayment={countBounds.editRequiresPayment}
          onApply={applyCounts}
        />
      )}

      {summary?.truncated && (
        <Alert>
          <span>
            Your text was longer than the free budget, so the quiz was built from the most representative
            parts. Some details from the end of your text may not be covered.
          </span>
        </Alert>
      )}

      {summary?.status === "failed" && quiz && (
        <Alert variant="destructive">
          <div className="flex flex-col gap-2">
            <span>{summary.error ?? "Some levels could not be built."}</span>
            <Button variant="secondary" size="sm" className="w-fit" onClick={retry}>
              <RefreshCw />
              Rebuild remaining levels
            </Button>
          </div>
        </Alert>
      )}

      {result ? (
        <ResultsView result={result} nextLevel={nextLevel} onNext={goToNextLevel} />
      ) : quiz ? (
        <QuizCard quiz={quiz} onSubmitted={onSubmitted} />
      ) : summary?.errorCode === "TRANSCRIPT_NEEDS_BROWSER" ? (
        <TranscriptFallback videoId={videoId} onRefresh={refresh} />
      ) : summary?.status === "failed" || error ? (
        <Alert variant="destructive">
          <div className="flex flex-col gap-2">
            <span>{summary?.error ?? error}</span>
            <Button variant="secondary" size="sm" className="w-fit" onClick={retry}>
              <RefreshCw />
              Try again
            </Button>
          </div>
        </Alert>
      ) : activeLevelStatus?.available && !activeLevelStatus.unlocked ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
            <Lock className="size-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Level {activeLevel} is locked. Pass Level {activeLevel - 1} to unlock it.
            </p>
          </CardContent>
        </Card>
      ) : loadingQuiz ? (
        <Card>
          <CardContent className="space-y-4 p-6">
            <Progress value={40} />
            <p className="pt-1 text-center text-sm text-muted-foreground">Loading…</p>
          </CardContent>
        </Card>
      ) : levelError ? (
        <Alert variant="destructive">
          <span>{levelError}</span>
        </Alert>
      ) : (
        <Card>
          <CardContent className="space-y-4 p-6">
            <ProgressStepper status={summary?.status ?? "queued"} />
            <p className="pt-1 text-center text-sm text-muted-foreground">
              {loading ? "Checking…" : "Almost there — this usually takes under a minute."}
            </p>
          </CardContent>
        </Card>
      )}

      {result && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => navigate("/")}>
            Make another quiz
          </Button>
        </div>
      )}
    </div>
  );
}